// Storage for an instructor invite token handed off across the register/confirm-email/login
// round trip. localStorage, not sessionStorage — the confirmation email link is commonly
// opened in a NEW tab (the user's mail client spawns one), which gets a fresh, empty
// sessionStorage for this origin even though it's the same browser. sessionStorage silently
// loses the token in that case, so the invitation is never actually accepted even though the
// user believes they completed it (the account still ends up created, just unaffiliated).
// Same reasoning and shape as src/lib/pendingInvite.ts, which already solved this for student
// invites; kept as a separate module since the token prefix and downstream consumers differ.

const TOKEN_KEY    = 'dt_pending_instructor_invite_token'
const SAVED_AT_KEY = 'dt_pending_instructor_invite_saved_at'

// Same generous window as pendingInvite.ts, for the same reason: this only needs to catch a
// token that was saved but never submitted (e.g. the "check your email" tab was closed), not
// defend against staleness on its own — every accept-invite ATTEMPT clears the token
// regardless of outcome (see AcceptInstructorInvite.tsx), except a wrong-account mismatch,
// which is deliberately retryable.
export const PENDING_INSTRUCTOR_INVITE_MAX_AGE_MS = 24 * 60 * 60 * 1000 // 24 hours

/** Pure: is a token saved at `savedAt` still fresh enough to act on, judged at `now`? */
export function isPendingInstructorInviteFresh(savedAt: number, now: number): boolean {
  if (!Number.isFinite(savedAt) || !Number.isFinite(now)) return false
  const age = now - savedAt
  return age >= 0 && age < PENDING_INSTRUCTOR_INVITE_MAX_AGE_MS
}

export function savePendingInstructorInviteToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(SAVED_AT_KEY, String(Date.now()))
  } catch { /* storage unavailable — token still usable for the rest of this page's lifetime */ }
}

/** Returns the token only if present and still fresh; clears it (and the timestamp) otherwise. */
export function readPendingInstructorInviteToken(now: number = Date.now()): string | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return null
    const savedAt = Number(localStorage.getItem(SAVED_AT_KEY))
    if (!isPendingInstructorInviteFresh(savedAt, now)) {
      clearPendingInstructorInviteToken()
      return null
    }
    return token
  } catch {
    return null
  }
}

export function clearPendingInstructorInviteToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(SAVED_AT_KEY)
  } catch { /* ignore */ }
}
